import * as log from '../util/log';
import { storableError } from '../util/errors';
import { clearCurrentUser, fetchCurrentUser } from './user.duck';
import {
  createUserWithIdp,
  createAutoListingApi,
  requestPasswordlessLinkApi,
  nativeLoginApi,
  nativeSignupApi,
  nativeLogoutApi,
} from '../util/api';
import { markReviewPromptFreshLogin } from '../util/reviewPromptFrequency';

const authenticated = authInfo => authInfo?.isAnonymous === false;
const loggedInAs = authInfo => authInfo?.isLoggedInAs === true;

// ================ Action types ================ //

export const AUTH_INFO_REQUEST = 'app/auth/AUTH_INFO_REQUEST';
export const AUTH_INFO_SUCCESS = 'app/auth/AUTH_INFO_SUCCESS';

export const LOGIN_REQUEST = 'app/auth/LOGIN_REQUEST';
export const LOGIN_SUCCESS = 'app/auth/LOGIN_SUCCESS';
export const LOGIN_ERROR = 'app/auth/LOGIN_ERROR';

export const LOGOUT_REQUEST = 'app/auth/LOGOUT_REQUEST';
export const LOGOUT_SUCCESS = 'app/auth/LOGOUT_SUCCESS';
export const LOGOUT_ERROR = 'app/auth/LOGOUT_ERROR';

export const SIGNUP_REQUEST = 'app/auth/SIGNUP_REQUEST';
export const SIGNUP_SUCCESS = 'app/auth/SIGNUP_SUCCESS';
export const SIGNUP_ERROR = 'app/auth/SIGNUP_ERROR';

export const CONFIRM_REQUEST = 'app/auth/CONFIRM_REQUEST';
export const CONFIRM_SUCCESS = 'app/auth/CONFIRM_SUCCESS';
export const CONFIRM_ERROR = 'app/auth/CONFIRM_ERROR';

export const PASSWORDLESS_REQUEST = 'app/auth/PASSWORDLESS_REQUEST';
export const PASSWORDLESS_SUCCESS = 'app/auth/PASSWORDLESS_SUCCESS';
export const PASSWORDLESS_ERROR = 'app/auth/PASSWORDLESS_ERROR';
export const PASSWORDLESS_RESET = 'app/auth/PASSWORDLESS_RESET';

// Generic user_logout action that can be handled elsewhere
// E.g. src/reducers.js clears store as a consequence
export const USER_LOGOUT = 'app/USER_LOGOUT';

// ================ Reducer ================ //

const initialState = {
  isAuthenticated: false,

  // is marketplace operator logged in as a marketplace user
  isLoggedInAs: false,

  // scopes associated with current token
  authScopes: [],

  // auth info
  authInfoLoaded: false,

  // login
  loginError: null,
  loginInProgress: false,

  // logout
  logoutError: null,
  logoutInProgress: false,

  // signup
  signupError: null,
  signupInProgress: false,

  // confirm (create use with idp)
  confirmError: null,
  confirmInProgress: false,

  // passwordless (magic-link) request
  passwordlessRequestInProgress: false,
  passwordlessRequestError: null,
  passwordlessRequestSent: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case AUTH_INFO_REQUEST:
      return state;
    case AUTH_INFO_SUCCESS:
      return {
        ...state,
        authInfoLoaded: true,
        isAuthenticated: authenticated(payload),
        isLoggedInAs: loggedInAs(payload),
        authScopes: payload.scopes,
      };

    case LOGIN_REQUEST:
      return {
        ...state,
        loginInProgress: true,
        loginError: null,
        logoutError: null,
        signupError: null,
      };
    case LOGIN_SUCCESS:
      return { ...state, loginInProgress: false, isAuthenticated: true };
    case LOGIN_ERROR:
      return { ...state, loginInProgress: false, loginError: payload };

    case LOGOUT_REQUEST:
      return { ...state, logoutInProgress: true, loginError: null, logoutError: null };
    case LOGOUT_SUCCESS:
      return {
        ...state,
        logoutInProgress: false,
        isAuthenticated: false,
        isLoggedInAs: false,
        authScopes: [],
      };
    case LOGOUT_ERROR:
      return { ...state, logoutInProgress: false, logoutError: payload };

    case SIGNUP_REQUEST:
      return { ...state, signupInProgress: true, loginError: null, signupError: null };
    case SIGNUP_SUCCESS:
      return { ...state, signupInProgress: false };
    case SIGNUP_ERROR:
      return { ...state, signupInProgress: false, signupError: payload };

    case CONFIRM_REQUEST:
      return { ...state, confirmInProgress: true, loginError: null, confirmError: null };
    case CONFIRM_SUCCESS:
      return { ...state, confirmInProgress: false, isAuthenticated: true };
    case CONFIRM_ERROR:
      return { ...state, confirmInProgress: false, confirmError: payload };

    case PASSWORDLESS_REQUEST:
      return { ...state, passwordlessRequestInProgress: true, passwordlessRequestError: null };
    case PASSWORDLESS_SUCCESS:
      return { ...state, passwordlessRequestInProgress: false, passwordlessRequestSent: true };
    case PASSWORDLESS_ERROR:
      return { ...state, passwordlessRequestInProgress: false, passwordlessRequestError: payload };
    case PASSWORDLESS_RESET:
      return {
        ...state,
        passwordlessRequestInProgress: false,
        passwordlessRequestError: null,
        passwordlessRequestSent: false,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const authenticationInProgress = state => {
  const { loginInProgress, logoutInProgress, signupInProgress, confirmInProgress } = state.auth;
  return loginInProgress || logoutInProgress || signupInProgress || confirmInProgress;
};

// ================ Action creators ================ //

export const authInfoRequest = () => ({ type: AUTH_INFO_REQUEST });
export const authInfoSuccess = info => ({ type: AUTH_INFO_SUCCESS, payload: info });

export const loginRequest = () => ({ type: LOGIN_REQUEST });
export const loginSuccess = () => ({ type: LOGIN_SUCCESS });
export const loginError = error => ({ type: LOGIN_ERROR, payload: error, error: true });

export const logoutRequest = () => ({ type: LOGOUT_REQUEST });
export const logoutSuccess = () => ({ type: LOGOUT_SUCCESS });
export const logoutError = error => ({ type: LOGOUT_ERROR, payload: error, error: true });

export const signupRequest = () => ({ type: SIGNUP_REQUEST });
export const signupSuccess = () => ({ type: SIGNUP_SUCCESS });
export const signupError = error => ({ type: SIGNUP_ERROR, payload: error, error: true });

export const confirmRequest = () => ({ type: CONFIRM_REQUEST });
export const confirmSuccess = () => ({ type: CONFIRM_SUCCESS });
export const confirmError = error => ({ type: CONFIRM_ERROR, payload: error, error: true });

export const userLogout = () => ({ type: USER_LOGOUT });

export const passwordlessRequest = () => ({ type: PASSWORDLESS_REQUEST });
export const passwordlessSuccess = () => ({ type: PASSWORDLESS_SUCCESS });
export const passwordlessError = error => ({ type: PASSWORDLESS_ERROR, payload: error, error: true });
export const passwordlessReset = () => ({ type: PASSWORDLESS_RESET });

// ================ Thunks ================ //

export const authInfo = () => (dispatch, getState, sdk) => {
  dispatch(authInfoRequest());
  return sdk
    .authInfo()
    .then(info => dispatch(authInfoSuccess(info)))
    .catch(e => {
      // Requesting auth info just reads the token from the token
      // store (i.e. cookies), and should not fail in normal
      // circumstances. If it fails, it's due to a programming
      // error. In that case we mark the operation done and dispatch
      // `null` success action that marks the user as unauthenticated.
      log.error(e, 'auth-info-failed');
      dispatch(authInfoSuccess(null));
    });
};

// Phase 5 (native session): login/signup/logout now go through
// server/api/auth/nativeSession.js instead of calling the Sharetribe SDK
// directly from the browser -- the server still verifies the credential
// against Sharetribe (and, for signup, mints the account's UUID there,
// per the Phase 5 constraint that userId stays Sharetribe-sourced), but
// the resulting session lives in the native cf_session/cf_refresh cookies,
// not in a Sharetribe cookie the browser SDK instance manages itself.
export const login = (username, password) => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(loginRequest());

  // Note that the thunk does not reject when the login fails, it
  // just dispatches the login error action.
  return nativeLoginApi({ username, password })
    .then(() => dispatch(fetchCurrentUser({ afterLogin: true })))
    .then(() => {
      const userId = getState().user?.currentUser?.id?.uuid;
      if (userId) {
        markReviewPromptFreshLogin(userId);
      }
      return dispatch(loginSuccess());
    })
    .catch(e => dispatch(loginError(storableError(e))));
};

export const logout = () => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(logoutRequest());

  // Note that the thunk does not reject when the logout fails, it
  // just dispatches the logout error action.
  return nativeLogoutApi()
    .then(() => {
      // The order of the dispatched actions
      dispatch(logoutSuccess());
      dispatch(clearCurrentUser());
      log.clearUserId();
      dispatch(userLogout());
    })
    .catch(e => dispatch(logoutError(storableError(e))));
};

export const signup = params => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(signupRequest());
  // Note: params are already structured on AuthenticationPage (handleSubmitSignup)

  // nativeSignupApi creates the Sharetribe account (minting its UUID) and
  // logs the new account in server-side in one request -- no separate
  // login() dispatch needed afterward, unlike the old direct-SDK flow.
  return nativeSignupApi(params)
    .then(() => dispatch(signupSuccess()))
    .then(() => dispatch(fetchCurrentUser({ afterLogin: true })))
    .then(() => {
      // Create default listing for the new user
      return createAutoListingApi({}).catch(err => {
        // Log error but don't fail the signup process
        log.error(err, 'auto-listing-creation-failed', {
          email: params.email,
        });
      });
    })
    .catch(e => {
      dispatch(signupError(storableError(e)));
      log.error(e, 'signup-failed', {
        email: params.email,
        firstName: params.firstName,
        lastName: params.lastName,
      });
    });
};

// Requests a login/signup link be emailed to the given address. Always
// resolves on the happy path -- the server responds success regardless of
// whether the email matches an account (anti-enumeration), so there's
// nothing to branch on here beyond "the request itself didn't error".
export const requestPasswordlessLink = ({ email, from, userType }) => dispatch => {
  dispatch(passwordlessRequest());
  return requestPasswordlessLinkApi({ email, from, userType })
    .then(() => dispatch(passwordlessSuccess()))
    .catch(e => dispatch(passwordlessError(storableError(e))));
};

export const signupWithIdp = params => (dispatch, getState, sdk) => {
  dispatch(confirmRequest());
  return createUserWithIdp(params)
    .then(res => {
      return dispatch(confirmSuccess());
    })
    .then(() => dispatch(fetchCurrentUser()))
    .then(() => {
      const userId = getState().user?.currentUser?.id?.uuid;
      if (userId) {
        markReviewPromptFreshLogin(userId);
      }
      // Create default listing for the new user
      return createAutoListingApi({}).catch(err => {
        // Log error but don't fail the signup process
        log.error(err, 'auto-listing-creation-failed-idp', {
          idpId: params.idpId,
        });
      });
    })
    .catch(e => {
      log.error(e, 'create-user-with-idp-failed', { params });
      return dispatch(confirmError(storableError(e)));
    });
};
